<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { SectionItem } from '@client/components/section';
  import { Spinner } from '@client/components/spinner';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  /**
   * Function to fetch the list of items with their priorities.
   * @type {Function | null}
   * @default null
   */
  export let getFunction = null;

  /**
   * Function to save the updated priority order.
   * @type {Function | null}
   * @default null
   */
  export let saveFunction = null;

  /**
   * Function to rollback priority changes.
   * @type {Function | null}
   * @default null
   */
  export let rollbackFunction = null;

  /**
   * Label used to describe items in the priority list (e.g., 'route', 'signature').
   * @type {string}
   * @default 'item'
   */
  export let itemLabel = 'item';

  let loading = true;
  let items = [];
  let originalItems = [];
  let draggedIndex = null;
  let dropIndex = null;
  let startPosition = null;
  let targetPosition = null;

  onMount(async () => {
    items = await getFunction();
    originalItems = [...items];
    loading = false;

    if (onExecute) {
      onExecute(executePriorityChange);
    }

    if (onRollback && rollbackFunction) {
      onRollback(rollbackFunction);
    }
  });

  const updatePriorityPositions = (list, start, target) => {
    const newList = [...list];

    if (start < target) {
      newList.splice(target + 1, 0, newList[start]);
      newList.splice(start, 1);
    } else {
      if (target === -1) {
        newList.splice(start - 1, 1);
      } else {
        newList.splice(target, 0, newList[start]);
        newList.splice(start + 1, 1);
      }
    }

    newList.forEach((item, index) => {
      item.priority = index + 1;
    });

    return newList;
  };

  const onDragStart = (index) => {
    draggedIndex = index;
  };

  const onDragOver = (index) => {
    dropIndex = index;
  };

  const onDrop = (index) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      items = updatePriorityPositions(items, draggedIndex, index);
    }
    draggedIndex = null;
    dropIndex = null;
  };

  const moveUp = (index) => {
    if (index > 0) {
      items = updatePriorityPositions(items, index, index - 1);
    }
  };

  const moveDown = (index) => {
    if (index < items.length - 1) {
      items = updatePriorityPositions(items, index, index + 1);
    }
  };

  const updatePositions = () => {
    if (startPosition && targetPosition) {
      items = updatePriorityPositions(items, Number(startPosition) - 1, Number(targetPosition) - 1);
      startPosition = null;
      targetPosition = null;
    }
  };

  const executePriorityChange = async () => {
    await saveFunction(items);
  };

  const revertChanges = () => {
    items = [...originalItems];
    startPosition = null;
    targetPosition = null;
  };

  $: hasChanges = JSON.stringify(items) !== JSON.stringify(originalItems);
  $: nextDisabled = !hasChanges || loading;
  $: step.data = { items, hasChanges };

  // Validate manual move positions
  $: isValidMove =
    startPosition &&
    targetPosition &&
    Number(startPosition) >= 1 &&
    Number(startPosition) <= items.length &&
    Number(targetPosition) >= 1 &&
    Number(targetPosition) <= items.length &&
    Number(startPosition) !== Number(targetPosition);
</script>

<div class="priority-change-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <div class="priority-order-section">
      <div class="section-header-row">
        <div class="header-left">
          <h3 class="section-title">Rearrange Priority</h3>
          <p class="description">Drag and drop, use the arrows, or manually reorder the {itemLabel}s</p>
        </div>

        <div class="header-right">
          <div class="manual-move">
            <div class="move-inputs">
              <div class="input-group">
                <label for="start-position">From</label>
                <input
                  id="start-position"
                  type="number"
                  bind:value={startPosition}
                  min="1"
                  max={items.length}
                  placeholder="Start"
                />
              </div>
              <div class="input-group">
                <label for="target-position">To</label>
                <input
                  id="target-position"
                  type="number"
                  bind:value={targetPosition}
                  min="1"
                  max={items.length}
                  placeholder="Target"
                />
              </div>
              <Button color="primary" size="sm" on:click={updatePositions} disabled={!isValidMove}>Move</Button>
            </div>
          </div>
        </div>
      </div>

      <div class="priority-list">
        {#each items as item, index}
          <div
            class="priority-item"
            class:dragging={draggedIndex === index}
            class:drop-target={dropIndex === index}
            draggable="true"
            role="button"
            tabindex="0"
            on:dragstart={() => onDragStart(index)}
            on:dragover|preventDefault={() => onDragOver(index)}
            on:drop|preventDefault={() => onDrop(index)}
            on:dragend={() => {
              draggedIndex = null;
              dropIndex = null;
            }}
          >
            <div class="priority-content">
              <div class="priority-badge" class:active={item.status}>{index + 1}</div>

              <div class="item-content">
                <slot name="item" {item} {index}>
                  <div class="default-item">
                    <strong>{item.label || item.name || item.value || item.blockname || 'Unknown'}</strong>
                  </div>
                </slot>
              </div>

              <div class="item-actions">
                <button
                  class="arrow-btn"
                  on:click|stopPropagation={() => moveUp(index)}
                  disabled={index === 0}
                  title="Move up"
                >
                  <Icon name="arrow-up" size="16" />
                </button>
                <button
                  class="arrow-btn"
                  on:click|stopPropagation={() => moveDown(index)}
                  disabled={index === items.length - 1}
                  title="Move down"
                >
                  <Icon name="arrow-down" size="16" />
                </button>
                <div class="drag-handle" title="Drag to reorder">
                  <Icon name="grip-vertical" size="16" />
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>

    {#if hasChanges}
      <div class="changes-notice">
        <div class="notice-content">
          <Icon name="info" size="16" />
          <span>Priority order has been modified. Click <b>Change Priority</b> to save changes.</span>
        </div>
        <Button color="secondary" size="sm" on:click={revertChanges}>
          <Icon name="rotate-ccw" size="14" />
          Revert Changes
        </Button>
      </div>
    {/if}
  {/if}
</div>

<style lang="scss">
  .priority-change-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }

    .priority-order-section {
      .section-header-row {
        display: grid;
        gap: 24px;
        grid-template-columns: 1fr 1fr;
        margin-bottom: var(--margin-large);

        .header-left {
          .section-title {
            color: var(--color-gray-900);
            font-size: var(--font-size-large);
            font-weight: var(--font-weight-medium);
            margin-bottom: var(--margin-small);
            margin-top: 0;
          }

          .description {
            margin-bottom: 0;
            margin-top: 0;
          }
        }

        .header-right {
          align-items: center;
          display: flex;
          padding: var(--padding-small);

          .manual-move {
            background-color: var(--color-gray-50);
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius-large);
            padding: var(--padding-base);
            width: 100%;

            .move-inputs {
              align-items: center;
              display: grid;
              gap: 12px;
              grid-template-columns: 1fr 1fr 0.8fr;

              .input-group {
                display: flex;
                flex-direction: column;
                gap: 4px;

                label {
                  color: var(--color-gray-600);
                  font-size: 12px;
                  font-weight: 500;
                  margin: 0;
                }

                input {
                  border: 1px solid var(--border-color);
                  border-radius: var(--border-radius-base);
                  font-size: 14px;
                  padding: 6px 10px;
                  width: 100%;

                  &:focus {
                    border-color: var(--color-blue-500);
                    outline: none;
                  }
                }
              }

              :global(.btn) {
                align-self: end;
                height: 31.5px;
                width: 100%;
              }
            }
          }
        }
      }
    }

    .priority-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: var(--padding-small);
    }

    .priority-item {
      background: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-large);
      cursor: grab;
      padding: var(--padding-large) var(--padding-xlarge);
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 4px var(--color-gray-100);
      }

      &.dragging {
        cursor: grabbing;
        opacity: 0.5;
      }

      &.drop-target {
        border-color: var(--color-blue-400);
        border-style: dashed;
      }

      .priority-content {
        align-items: center;
        display: grid;
        font-size: var(--font-size-medium);
        gap: 24px;
        grid-template-columns: 32px minmax(min-content, 1fr) auto;
        position: relative;

        .priority-badge {
          align-items: center;
          background-color: var(--color-gray-400);
          border-radius: var(--border-radius-base);
          color: var(--color-white);
          display: inline-flex;
          font-weight: var(--font-weight-medium);
          height: 30px;
          justify-content: center;
          padding: var(--padding-small) var(--padding-base);
          transition: all 0.3s ease-in-out;
          width: 30px;

          &.active {
            background-color: var(--color-blue-400);
          }
        }

        .item-content {
          flex: 1;
          min-width: 0;
        }

        .default-item {
          strong {
            color: var(--color-gray-800);
            font-size: var(--font-size-medium);
          }
        }

        .item-actions {
          align-items: center;
          display: flex;
          flex-shrink: 0;
          gap: 4px;

          .arrow-btn {
            align-items: center;
            background-color: var(--color-white);
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius-base);
            color: var(--color-gray-600);
            cursor: pointer;
            display: flex;
            height: 28px;
            justify-content: center;
            padding: 0;
            transition: all 0.2s ease;
            width: 28px;

            &:hover:not(:disabled) {
              background-color: var(--color-gray-100);
              border-color: var(--color-gray-400);
              color: var(--color-gray-800);
            }

            &:disabled {
              cursor: not-allowed;
              opacity: 0.4;
            }
          }

          .drag-handle {
            align-items: center;
            color: var(--color-gray-400);
            cursor: grab;
            display: flex;
            height: 28px;
            justify-content: center;
            margin-left: 4px;
            width: 20px;

            &:hover {
              color: var(--color-gray-600);
            }
          }
        }
      }
    }

    .changes-notice {
      align-items: center;
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-large);
      color: var(--color-blue-700);
      display: flex;
      font-size: 14px;
      gap: 16px;
      justify-content: space-between;
      margin-top: 16px;
      padding: 12px;

      .notice-content {
        align-items: center;
        display: flex;
        gap: 8px;
      }

      :global(.btn) {
        align-items: center;
        display: flex;
        gap: 6px;
        white-space: nowrap;
      }
    }
  }
</style>
